import { Timestamp } from '@bufbuild/protobuf';
import { Code, ConnectError } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PROGRAM_SUMMARY_KEY } from '../../../components/delivery/widgets/useProgramFinancialSummary';
import { deliveryClient } from '../../../lib/connect/clients';
// ============================================================================
// CPARSScore — Upsert/List/Delete. Delete requires the same server-computed
// "close" transition authority (program manager / quality) as the RPC itself
// enforces — see canDeleteQualityScore on GetProgramResponse.
// ============================================================================
const CPARS_KEY = 'cpars score';
export function useCPARSScores(programId) {
    return useQuery({
        queryKey: [
            CPARS_KEY,
            'list',
            programId
        ],
        queryFn: async ()=>{
            if (!programId) return {
                items: []
            };
            const resp = await deliveryClient.listCPARSScores({
                programId
            });
            return {
                items: resp.scores
            };
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
export function useUpsertCPARSScore() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const resp = await deliveryClient.upsertCPARSScore({
                id: input.id,
                programId: input.programId,
                // No entryNumber: the server assigns it on create and preserves it on
                // update (SAD-171) — it ignores this field entirely, so this caller
                // has nothing to contribute there.
                overallScore: input.overallScore,
                evalPeriodStart: Timestamp.fromDate(input.evalPeriodStart),
                evalPeriodEnd: Timestamp.fromDate(input.evalPeriodEnd),
                overallComment: input.overallComment,
                qualityScore: input.qualityScore,
                scheduleScore: input.scheduleScore,
                costControlScore: input.costControlScore,
                managementScore: input.managementScore,
                smallBusinessScore: input.smallBusinessScore,
                regulatoryComplianceScore: input.regulatoryComplianceScore,
                qualityNarrative: input.qualityNarrative,
                scheduleNarrative: input.scheduleNarrative,
                costControlNarrative: input.costControlNarrative,
                managementNarrative: input.managementNarrative,
                smallBusinessNarrative: input.smallBusinessNarrative,
                regulatoryComplianceNarrative: input.regulatoryComplianceNarrative,
                getWellPlan: input.getWellPlan,
                attachmentId: input.attachmentId,
                concurrenceStatus: input.concurrenceStatus,
                concurrenceComment: input.concurrenceComment
            });
            return resp.score;
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    CPARS_KEY
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    PROGRAM_SUMMARY_KEY
                ]
            });
            toast.success('CPARS score saved');
        },
        onError: (err)=>{
            // The natural key — (tenant, program, eval period start, eval period
            // end) — is enforced both by a pre-check and the DB index, either of
            // which surfaces here as AlreadyExists (see cparsNaturalKeyConflictMessage
            // in ryugu's quality.go). The entry number is system-assigned (SAD-171)
            // and not something the user can change, so name only what they can
            // actually act on: the evaluation period, or the existing record.
            if (err instanceof ConnectError && err.code === Code.AlreadyExists) {
                toast.error('A CPARS score for that evaluation period already exists. ' + 'Edit the existing record, or change the evaluation period ' + 'to save this as a new one.');
                return;
            }
            toast.error(`Failed to save CPARS score: ${err.message}`);
        }
    });
}
export function useDeleteCPARSScore() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            await deliveryClient.deleteCPARSScore({
                programId: input.programId,
                id: input.id
            });
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    CPARS_KEY
                ]
            });
            // A deleted score changes the Customer KPI rollup.
            qc.invalidateQueries({
                queryKey: [
                    PROGRAM_SUMMARY_KEY
                ]
            });
            toast.success('CPARS score deleted');
        },
        onError: (err)=>{
            toast.error(`Failed to delete CPARS score: ${err.message}`);
        }
    });
}
// ============================================================================
// QASPScore — Upsert/List/Delete. Delete requires the same server-computed
// "close" transition authority (program manager / quality) as the RPC itself
// enforces — see canDeleteQualityScore on GetProgramResponse.
// ============================================================================
const QASP_KEY = 'qasp score';
export function useQASPScores(programId) {
    return useQuery({
        queryKey: [
            QASP_KEY,
            'list',
            programId
        ],
        queryFn: async ()=>{
            if (!programId) return {
                items: []
            };
            const resp = await deliveryClient.listQASPScores({
                programId
            });
            return {
                items: resp.scores
            };
        },
        enabled: !!programId,
        staleTime: 30000
    });
}
export function useUpsertQASPScore() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            const resp = await deliveryClient.upsertQASPScore({
                id: input.id,
                programId: input.programId,
                quarter: input.quarter,
                overallScore: input.overallScore,
                technicalScore: input.technicalScore,
                scheduleScore: input.scheduleScore,
                costStaffingScore: input.costStaffingScore,
                managementScore: input.managementScore,
                technicalNarrative: input.technicalNarrative,
                scheduleNarrative: input.scheduleNarrative,
                costStaffingNarrative: input.costStaffingNarrative,
                managementNarrative: input.managementNarrative,
                reportPeriodStart: Timestamp.fromDate(input.reportPeriodStart),
                reportPeriodEnd: Timestamp.fromDate(input.reportPeriodEnd),
                getWellPlan: input.getWellPlan,
                attachmentId: input.attachmentId
            });
            return resp.score;
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    QASP_KEY
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    PROGRAM_SUMMARY_KEY
                ]
            });
            toast.success('QASP score saved');
        },
        onError: (err)=>{
            // Migration 000139's natural key — (tenant, program, quarter, report
            // period) — is enforced both by a pre-check and the DB index, either of
            // which surfaces here as AlreadyExists (see qaspNaturalKeyConflictMessage
            // in ryugu's quality.go). Name the fix, not just the collision.
            if (err instanceof ConnectError && err.code === Code.AlreadyExists) {
                toast.error('A QASP score for that quarter and report period already exists. ' + 'Edit the existing record, or change the quarter or report period ' + 'to save this as a new one.');
                return;
            }
            toast.error(`Failed to save QASP score: ${err.message}`);
        }
    });
}
export function useDeleteQASPScore() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input)=>{
            await deliveryClient.deleteQASPScore({
                programId: input.programId,
                id: input.id
            });
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    QASP_KEY
                ]
            });
            // A deleted score changes the Customer KPI rollup.
            qc.invalidateQueries({
                queryKey: [
                    PROGRAM_SUMMARY_KEY
                ]
            });
            toast.success('QASP score deleted');
        },
        onError: (err)=>{
            toast.error(`Failed to delete QASP score: ${err.message}`);
        }
    });
}
